import { useReducer, useMemo } from 'react';
import type { WatchlistItem } from '@/hooks/useWatchlist';

const actionTypes = {
  toggleAnalysts: 'TOGGLE_ANALYSTS',
  toggleValuation: 'TOGGLE_VALUATION',
  toggleFairValuation: 'TOGGLE_FAIR_VALUE',
  closeFairValuation: 'CLOSE_FAIR_VALUE',
} as const;

export interface ModalState {
  isAnalystsModalOpen: boolean;
  isValuationModalOpen: boolean;
  isFairValuationModalOpen: boolean;
  activeWatchlistItem: WatchlistItem | null;
}

interface Action {
  type: (typeof actionTypes)[keyof typeof actionTypes];
  payload?: WatchlistItem;
}

export const initialModalState = {
  isAnalystsModalOpen: false,
  isValuationModalOpen: false,
  isFairValuationModalOpen: false,
  activeWatchlistItem: null,
};

function toggleReducer(state: ModalState, action: Action): ModalState {
  switch (action.type) {
    case actionTypes.toggleAnalysts:
      return { ...state, isAnalystsModalOpen: !state.isAnalystsModalOpen };
    case actionTypes.toggleValuation:
      return { ...state, isValuationModalOpen: !state.isValuationModalOpen };
    case actionTypes.toggleFairValuation:
      return {
        ...state,
        activeWatchlistItem: action.payload ?? null,
        isFairValuationModalOpen: !state.isFairValuationModalOpen,
      };
    case actionTypes.closeFairValuation:
      return {
        ...state,
        activeWatchlistItem: null,
        isFairValuationModalOpen: false,
      };
    default:
      throw new Error(`Unhandled type: ${action.type}`);
  }
}

export default function useExternalModals() {
  const [state, dispatch] = useReducer(toggleReducer, { ...initialModalState });

  return useMemo(() => {
    const openAnalystsModal = () => {
      dispatch({ type: actionTypes.toggleAnalysts });
    };

    const openValuationModal = () => {
      dispatch({ type: actionTypes.toggleValuation });
    };

    const openFairvalueModal = (payload: WatchlistItem) => {
      dispatch({
        type: actionTypes.toggleFairValuation,
        payload,
      });
    };

    const closeFairValueModal = () => {
      dispatch({ type: actionTypes.closeFairValuation });
    };

    return {
      state,
      actions: {
        openAnalystsModal,
        openValuationModal,
        openFairvalueModal,
        closeFairValueModal,
      },
    };
  }, [state, dispatch]);
}
